import {EventEmitter, Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {UrlService} from './url.service';
import {Building} from '../types/models';
import {Document} from '../types/document-data.types';
import {EmptyRowService} from './empty-row.service';
import {Observable} from 'rxjs/Observable';
import {Headers, Http, RequestOptions} from '@angular/http';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {ActivityDictionary} from '../views/directory-item/activity/activity.dictionary';
import {AccountPermissionsService} from './account-permissions.service';
import {AuthenticationService} from './authentication.service';
import {environment} from 'environments/environment';
import {HistoryItem} from '../views/directory-item/activity/history-item.class';
import {AuthTokenService} from './auth-token.service';
import {map, tap} from 'rxjs/operators';
import {BuildingApiService} from './api/building-api.service';
import {of} from 'rxjs/observable/of';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';
import {IBuilding} from '../types/api-response/building';
import {NetworkEntity} from '../types/data.types';
import {DocumentApiService} from './api/document-api.service';

declare const toastr: any;
declare const $: any;
declare const swal: any;

const baseUrl = '/api/v1/documents.json';

@Injectable()
export class DocumentsService {
    public isDocumentViewModalShow: boolean = false;
    public isCreateFolderModalShow: boolean = false;
    public isMoveModalShow: boolean = false;
    public isUploadFileModalShow: boolean = false;
    public isEditPermissionModalShow: boolean = false;
    public documentInViewModal: Document;
    public currentItem;
    public isBuildingLoading: boolean = true;
    private rootDocument = 1;
    public documentsList: Array<Document> = [];
    public isDocumentsLoading: boolean = true;
    public isUpdating: boolean = false;
    public isOneBuilding: boolean = false;
    public isDocumentDetailedModalShow: boolean = false;

    public path: Array<Document> = [];

    public currentDirectoryId: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    isCorporationsListLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    corporationsListLoaded = this.isCorporationsListLoaded.asObservable();

    isPermissionChanged = false;

    buildings: Building[] = [];
    buildingsForRead: Building[] = [];
    buildingsForUpdate: Building[] = [];

    data: { total: number } = {total: null};
    serviceName: string = 'documents';
    selectedItemData = null;
    activities = [];
    spinnerShow = false;
    corporations = [];
    multiselectBuildings = [];
    dataToggled: EventEmitter<any> = new EventEmitter();
    userGroups = [];

    itemCache = {
        corporationNumbers: null,
        buildings: null,
        sharing: {
            isPublic: null,
            documentsPermissions: null,
            userGroups: null
        }
    };
    copyObject = {
        corporationNumbers: null,
        buildings: null
    };
    copyObjectEditPermissionModal = {
        corporationNumbers: [],
        buildings: []
    };
    formFields = {
        corporations: false,
        buildings: false,
        sharing: false,
        acceptableFrom: false,
        acceptableTo: false
    };
    multiselectSettings: any = {
        singleSelection: false,
        text: 'Select',
        selectAllText: 'Select All',
        unSelectAllText: 'Deselect All',
        enableSearchFilter: true
    };

    constructor(
        public api: ApiClientService,
        private http: Http,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService,
        private authenticationService: AuthenticationService,
        private authTokenService: AuthTokenService,
        private buildingApiService: BuildingApiService,
        private documentApiService: DocumentApiService
    ) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    showImage(document: Document) {
        this.isDocumentViewModalShow = true;
        this.documentInViewModal = new Document(document);
    }

    hideImage() {
        this.isDocumentViewModalShow = false;
        this.urlService.modalClosed.emit();
    }

    showCreateFolderModal() {
        this.isCreateFolderModalShow = true;
    }

    hideCreateFolderModal() {
        this.isCreateFolderModalShow = false;
        this.urlService.modalClosed.emit();
    }

    showUploadFileModal() {
        this.isUploadFileModalShow = true;
    }

    hideUploadFileModal() {
        this.isUploadFileModalShow = false;
        this.urlService.modalClosed.emit();
    }

    showMoveModal(item: Document) {
        this.isMoveModalShow = true;
        this.currentItem = item;
    }

    showEditPermissionModal(item: Document) {
        this.isEditPermissionModalShow = true;
        this.copyObjectEditPermissionModal.corporationNumbers = JSON.parse(JSON.stringify(this.selectedItemData.corporationNumbers));
        this.copyObjectEditPermissionModal.buildings = JSON.parse(JSON.stringify(this.selectedItemData.buildings));
        this.currentItem = item;
    }

    hideEditPermissionModal() {
        this.isEditPermissionModalShow = false;
        if (this.isPermissionChanged) {
            this.selectedItemData.corporationNumbers = JSON.parse(JSON.stringify(this.copyObjectEditPermissionModal.corporationNumbers));
            this.selectedItemData.buildings = JSON.parse(JSON.stringify(this.copyObjectEditPermissionModal.buildings));
            this.isPermissionChanged = false;
        }
        this.currentItem = null;
        this.urlService.modalClosed.emit();
    }

    getBuildings() {
        if (this.buildings.length) {
            return of(this.buildings);
        }
        return this.buildingApiService.getBuildings({
            'per-page': 10000,
            'order-by': 'name|asc'
        }, true)
            .pipe(
                createInitialNetworkEntity<IBuilding>(),
                resolveNetworkEntity((building: IBuilding) => Building.fromShortBuildingData(building)),
                tap(({loading}) => this.isBuildingLoading = loading),
                tap(
                    (res: NetworkEntity<Building>) => {
                        this.buildings = res.entities;
                        this.multiselectBuildings = [...this.buildings];
                        this.isOneBuilding = this.buildings.length === 1;
                        this.getFilteredBuildings();
                    },
                    () => this.isBuildingLoading = false
                ),
                map(response => response.entities)
            );
    }

    getCorporations() {
        if (this.corporations.length) return of(this.corporations);
        return this.buildingApiService.getCorporationNumberList({
            'per-page': 10000,
            'order-by': 'name|asc'
        }, false)
            .pipe(
                resolveNetworkEntity(c => ({
                    ...c,
                    itemName: c.name
                })),
                tap(
                    res => {
                        this.corporations = res.entities;
                        this.isCorporationsListLoaded.next(true);
                    },
                    error => this.api.errorHandler(error, 'Failed get buildings')
                ),
                map(res => res.entities)
            );
    }

    getCurrentBuildingFilter(): any {
        return this.buildings.find((item) => {
            return item.id === this.urlService.getQueryParamsJson().building;
        });
    }

    public getDocumentsByParentId(directoryId: number): Observable<Array<Document>> {
        this.isDocumentsLoading = true;

        if (this.documentsList.length <= 1) {
            if (this.urlService.queryParams.page > 1) {
                this.urlService.queryParams.page--;
            }
        }

        const queryParams = this.urlService.getQueryParamsString();
        return this.fetchDocuments(queryParams, directoryId).map((res) => {
            this.data.total = res.total;

            const document = this.findDocument(directoryId);

            if (this.documentsList.length && document) {
                document.children = res.entities;
            } else {
                this.documentsList = res.entities;
            }

            this.emptyRowService.fillEmptyRow();

            this.isDocumentsLoading = false;

            return res.entities;
        });
    }

    public async goTo(documentId?: number | string) {
        if (typeof documentId === 'string' && documentId === 'root') {
            documentId = this.rootDocument;
        }

        if (documentId) {
            if (this.urlService.queryParams.query) {
                this.resetPath();
            }

            this.urlService.resetSearchQuery();
            this.urlService.resetPageQuery();
        } else if (this.currentDirectoryId.getValue()) {
            documentId = this.currentDirectoryId.getValue();
        } else {
            documentId = this.rootDocument;
        }

        this.currentDirectoryId.next(+documentId);
        this.updatePath(+documentId);
    }

    public updatePath(documentId: number): void {
        const document = this.findDocument(documentId);

        if (document) {
            for (let i = 0; i < this.path.length; i++) {
                if (this.path[i].id === document.id) {
                    this.path.length = i + 1;

                    return;
                }
            }

            this.path.push(document);
        } else {
            this.resetPath();
        }
    }

    public resetPath(): void {
        this.path = [];
    }

    private findDocument(documentId: number, callback?: Function, child?): Document {
        const documents = child || this.documentsList;

        for (let i = 0; i < documents.length; i++) {
            if (String(documents[i].id) === String(documentId)) {
                if (callback) callback(documents[i]);

                return documents[i];
            } else {
                if (documents[i].children && documents[i].children.length) {
                    return this.findDocument(documentId, callback, documents[i].children);
                }
            }
        }

        return;
    }

    public getParentPermissions(): Observable<any> {
        const parent = this.findDocument(this.currentDirectoryId.getValue());

        const parentPermission: Array<any> = parent ? parent.documentsPermissions : [{permission: 'all'}];

        if (parentPermission.length) {
            const permissions: string[] = parentPermission.map(p => p.permission);

            return this.documentApiService.getPermissions(permissions)
                .pipe(
                    map((result) => {
                        result = result.filter(item => item !== 'staff_only');
                        return {
                            permissionsList: result,
                            parentPermission: parentPermission.map(item => item.permission),
                            documentsPermissions: parent !== undefined ? parent.documentsPermissions : null,
                            userGroups: parent !== undefined ? parent.userGroups : null,
                            isPublic: parent !== undefined ? parent.isPublic : null
                        };
                    })
                );
        }

        return new BehaviorSubject<any>({
            permissionsList: [],
            parentPermission: [],
            documentsPermissions: parent !== undefined ? parent.documentsPermissions : null,
            userGroups: parent !== undefined ? parent.userGroups : null,
            isPublic: parent !== undefined ? parent.isPublic : null
        });
    }

    public getParentCorporations(corporationNumbers): any {
        const parent: any = this.findDocument(this.currentDirectoryId.getValue());
        return corporationNumbers.filter((item) => {
            let hasMatchedCorporation = false;
            if (parent) {
                parent.corporationNumbers.forEach(corporation => {
                    if (String(item.id) === String(corporation.bdId))
                        hasMatchedCorporation = true;
                });
            }
            return hasMatchedCorporation;
        });
    }

    public getParentBuildings(): any {
        const parent: any = this.findDocument(this.currentDirectoryId.getValue());
        return this.buildings.filter((item) => {
            let hasMatchedBuilding = false;
            if (parent) {
                parent.buildings.forEach(building => {
                    if (String(item.id) === String(building.bdId))
                        hasMatchedBuilding = true;
                });
            }
            return hasMatchedBuilding;
        });
    }

    public parsePermission(permission): any {
        if (typeof permission === 'object') {
            permission = permission.permission;
        }

        return {id: permission, itemName: ActivityDictionary.getValue(permission)};
    }

    public createFolder(data): Observable<any> {
        const url = '/api/v1/documents';

        const body = {
            corporationNumbers: data.corporationNumbers.map(corporation => ({
                bdId: corporation.id,
                name: corporation.name
            })),
            buildings: data.buildings.map(building => ({
                bdId: building.id,
                name: building.name
            })),
            userGroups: data.userGroups,
            name: data.name,
            parent: this.currentDirectoryId.getValue(),
            type: 'directory',
            documentsPermissions: data.permissions,
            public: data.isPublic ? 1 : 0,
            acceptableFrom: data.acceptableFrom,
            acceptableTo: data.acceptableTo,
        };

        return this.api.post('domainDocuments', url, body).map(() => {
            toastr.success('Folder was created!');

            this.goTo();
        }, error => this.api.errorHandler(error, 'Failed create folder'));
    }

    public uploadFile(data): Observable<any> {
        const file = data.file;
        const fd = new FormData();

        fd.append('fileUpload', file);
        fd.append('_format', 'json');

        const token = this.authTokenService.accessToken;
        const headers = new Headers({'Authorization': `Bearer ${token}`});
        const options = new RequestOptions({headers: headers});

        return new Observable(observer => {
            const url = `${environment.domainDocuments}/api/_uploader/attachments/upload`;

            this.http.post(url, fd, options).subscribe((response: any) => {
                response = JSON.parse(response._body);

                const url = '/api/v1/documents';

                const body = {
                    corporationNumbers: data.corporationNumbers.map(corporation => ({
                        bdId: corporation.id,
                        name: corporation.name
                    })),
                    buildings: data.buildings.map(building => ({
                        bdId: building.id,
                        name: building.name
                    })),
                    name: data.name ? data.name : response.name,
                    path: response.path,
                    size: data.file.size,
                    parent: this.currentDirectoryId.getValue(),
                    type: 'file',
                    documentsPermissions: data.permissions,
                    public: data.isPublic ? 1 : 0
                };

                this.api.post('domainDocuments', url, body).subscribe((res) => {
                    observer.next(res);
                    observer.complete();
                }, error => this.api.errorHandler(error, 'Failed upload file'));
            }, error => this.api.errorHandler(error, 'Failed upload file'));
        });
    }

    uploadMultipleFiles(files): Observable<any> {
        return new Observable(observer => {
            const observables = [];
            const externalServerUploads = [];
            files.forEach(data => {
                const file = data.file;
                const fd = new FormData();

                fd.append('fileUpload', file);
                fd.append('_format', 'json');

                const token = this.authTokenService.accessToken;
                const headers = new Headers({'Authorization': `Bearer ${token}`});
                const options = new RequestOptions({headers: headers});
                const url = `${environment.domainDocuments}/api/_uploader/attachments/upload`;

                externalServerUploads.push(this.http.post(url, fd, options));
            });
            Observable.forkJoin(externalServerUploads).subscribe((resp: any) => {
                resp.forEach((response, index) => {
                    response = JSON.parse(response._body);

                    const url = '/api/v1/documents';

                    const body = {
                        corporationNumbers: files[index].corporationNumbers.map(corporation => ({
                            bdId: corporation.id,
                            name: corporation.name
                        })),
                        buildings: files[index].buildings.map(building => ({
                            bdId: building.id,
                            name: building.name
                        })),
                        name: files[index].name ? files[index].name : response.name,
                        path: response.path,
                        size: files[index].file.size,
                        parent: this.currentDirectoryId.getValue(),
                        type: 'file',
                        documentsPermissions: files[index].permissions,
                        public: files[index].isPublic ? 1 : 0,
                        userGroups: files[index].userGroups,
                        notify: files[index].notify ? 1 : 0,
                    };

                    if (files[index].notify) {
                        body['senderEmail'] = files[index].senderEmail;
                        body['subject'] = files[index].subject;
                        body['message'] = files[index].message;
                        body['saveAsDefault'] = Number(files[index].saveAsDefault);
                    }

                    if (files[index].acceptableFrom) {
                        body['acceptableFrom'] = files[index].acceptableFrom;
                    }

                    if (files[index].acceptableTo) {
                        body['acceptableTo'] = files[index].acceptableTo;
                    }

                    observables.push(this.api.post('domainDocuments', url, body));
                });
                const observablesResults = {
                    done: 0,
                    errors: 0
                };
                this.observablesQueue(observables, observablesResults, observer);
            }, error => this.api.errorHandler(error, 'Failed upload file'));
        });
    }

    remove(itemData) {
        const url = `/api/v1/documents/${itemData.id}`;
        const itemType = itemData.type === 'file' ? 'File' : 'Folder';
        this.api.remove('domainDocuments', url).subscribe(() => {
            toastr.success(`${itemType} was deleted!`);
            if (this.documentsList.length - 1 == 0 && this.urlService.queryParams.page != '1')
                this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
            this.goTo();
        }, error => this.api.errorHandler(error, `Failed delete ${itemType}!`));

    }

    public fetchDocuments(params: string = '', parent?: number): Observable<any> {
        const parentParam = parent && params.indexOf('query') < 0 ? `&parent_id=${parent}` : '';
        const resident = this.authenticationService.userIsResident;
        const selectedBuilding = JSON.parse(localStorage.getItem('residentselectedBuilding'));
        let sortedDateResident = '';
        if (resident && selectedBuilding) {
            sortedDateResident = `&building_id=${selectedBuilding.buildingId}`;
        }

        const url = `${baseUrl}${params}&expand=building,buildings,corporationNumbers,userGroups,createdBy,documentsPermissions${parentParam}${resident ? sortedDateResident : ''}`.replace('.json&', '.json?');

        return this.api.get('domainDocuments', url).map(
            res => JSON.parse(res._body),
            error => this.api.errorHandler(error, 'Failed get Documents')
        ).map(res => {
            res.entities.forEach(item => {
                item.isPublic = item.public;
                item.buildings.forEach(building => {
                    building.id = building.bdId;
                });
            });
            this.account.addEntitiesPermissions(res.entities, this.serviceName);
            return res;
        });
    }

    public fetchDocument(documentId: number): Observable<any> {
        const url = `/api/v1/documents/${documentId}?expand=building,buildings,corporationNumbers,userGroups,createdBy,documentsPermissions`;
        return this.api.getJson('domainDocuments', url).map(item => {
            item.isPublic = item.public;
            item.buildings.forEach(building => {
                building.id = building.bdId;
            });
            this.account.addPermissionsToEntity(item, this.serviceName);
            return item;
        });
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    observablesQueue(observables, observablesResults, observer) {
        observables[observablesResults.done].subscribe(
            res => {
                observablesResults.done++;
                if (observablesResults.done == observables.length) {
                    observer[observablesResults.errors < 1 ? 'next' : 'error'](res);
                    observer.complete();
                } else
                    this.observablesQueue(observables, observablesResults, observer);
            }, err => {
                observablesResults.done++;
                observablesResults.errors++;
                if (observablesResults.done == observables.length) {
                    observer[observablesResults.errors < 1 ? 'next' : 'error']();
                    observer.complete();
                } else
                    this.observablesQueue(observables, observablesResults, observer);
            }
        );
    }

    toggleDetailedView(data): void {
        this.dataToggled.emit();
        this.activities = [];
        this.formFields.corporations = false;
        this.formFields.buildings = false;
        this.formFields.sharing = false;
        if (data && (!this.selectedItemData || this.selectedItemData.id != data.id)) {
            this.selectedItemData = null;
            this.isDocumentDetailedModalShow = true;
            this.setItemCache(this.clearItemCache());
            data.checked = true;
            setTimeout(() => {
                this.selectedItemData = JSON.parse(JSON.stringify(data));
                this.selectedItemData.corporationNumbers = this.selectedItemData.corporationNumbers.map(c => ({
                    ...c,
                    id: c.bdId,
                    itemName: c.name
                }));
                this.selectedItemData.buildings = this.selectedItemData.buildings.map(b => ({
                    ...b,
                    itemName: b.name
                }));
                this.selectedItemData.documentsPermissions = this.selectedItemData.documentsPermissions.map(p => ({
                    ...p,
                    id: p.permission,
                    itemName: ActivityDictionary.getValue(p.permission)
                }));
                this.selectedItemData.userGroups = this.selectedItemData.userGroups.map(role => ({
                    id: role.bdId,
                    itemName: role.name
                }));
                this.multiselectBuildings = this.buildings.filter(b => this.buildingIsInCorporation((<any>b).corporationNumber, this.selectedItemData.corporationNumbers));
                this.getActivity();
                for (const field in this.selectedItemData.formFields)
                    this.selectedItemData.formFields[field].editMode = false;

                this.setItemCache({
                    corporationNumbers: JSON.parse(JSON.stringify(this.selectedItemData.corporationNumbers)),
                    buildings: JSON.parse(JSON.stringify(this.selectedItemData.buildings)),
                    sharing: {
                        isPublic: JSON.parse(JSON.stringify(this.selectedItemData.isPublic)),
                        documentsPermissions: JSON.parse(JSON.stringify(this.selectedItemData.documentsPermissions)),
                        userGroups: JSON.parse(JSON.stringify(this.selectedItemData.userGroups))
                    }
                });
            });
        } else if (this.selectedItemData && data && this.selectedItemData.id == data.id) {
            this.selectedItemData = null;
            this.isDocumentDetailedModalShow = false;
            this.setItemCache(this.clearItemCache());
        } else {
            this.isDocumentDetailedModalShow = false;
            this.selectedItemData = null;
            this.setItemCache(this.clearItemCache());
        }
    }

    getActivity() {
        if (this.selectedItemData) {
            this.spinnerShow = true;
            this.documentApiService.getHistory({
                'per-page': 10000,
                'order-by': 'loggedAt|desc',
                objectId: this.selectedItemData.id
            })
                .pipe(
                    resolveNetworkEntity(
                        a => new HistoryItem(a)
                    ),
                )
                .pipe(
                    tap(
                        res => this.activities = res.entities,
                        error => this.api.errorHandler(error, 'Failed get Document activity log'),
                        () => this.spinnerShow = false
                    ),
                ).subscribe();
        }
    }

    buildingIsInCorporation(id, array) {
        return array.find(item => item.id == id);
    }

    getUserGroups() {
        return this.buildingApiService.getUserGroups({'per-page': 1000})
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity(role => ({id: role.id.toString(), itemName: role.name})),
                tap(
                    res => this.userGroups = res.entities,
                    error => this.api.errorHandler(error, 'Failed get Roles')
                )
            );
    }

    canEdit(buildingId?: number) {

        return this.account.canUpdate(this.serviceName, buildingId);
    }

    canDelete(buildingId?: number) {

        return this.account.canDelete(this.serviceName, buildingId);
    }

    compare(a1, a2) {
        return a1.length == a2.length && a1.every((v, i) => JSON.stringify(v) === JSON.stringify(a2[i]));
    }

    getCorporationBuildingsData() {
        return {
            corporationNumbers: this.selectedItemData.corporationNumbers.map(c => ({
                bdId: c.id,
                name: c.name
            })),
            buildings: this.selectedItemData.buildings.map(b => ({
                bdId: b.id,
                name: b.name
            }))
        };
    }

    update(data, edit = false) {
        if (this.isUpdating) return;
        this.isUpdating = true;

        let applyCascade = 0;

        const currentItem = edit ? data : this.selectedItemData;
        if (edit) {
            this.itemCache.corporationNumbers = this.itemCache.corporationNumbers.map(c => ({
                bdId: c.id,
                name: c.name
            }));
            this.itemCache.buildings = this.itemCache.buildings.map(b => ({
                bdId: b.id,
                name: b.name
            }));
            this.itemCache.sharing.documentsPermissions = this.itemCache.sharing.documentsPermissions.map((item) => ({permission: item.id}));
            this.itemCache.sharing.userGroups = this.itemCache.sharing.userGroups.map(g => ({
                bdId: g.id,
                name: g.itemName
            }));
        }

        if (this.selectedItemData.type == 'directory' && this.selectedItemData.childrenCount) {
            let isNeedToUpdate = false;
            if (this.itemCache.corporationNumbers != null && this.itemCache.buildings != null) {
                if (!this.compare(currentItem.corporationNumbers, this.itemCache.corporationNumbers) ||
                    !this.compare(currentItem.buildings, this.itemCache.buildings)
                ) {
                    isNeedToUpdate = true;
                }
            }
            if (this.itemCache.sharing.isPublic != null &&
                this.itemCache.sharing.documentsPermissions != null &&
                this.itemCache.sharing.userGroups != null
            ) {
                if (currentItem.isPublic != this.itemCache.sharing.isPublic ||
                    !this.compare(currentItem.documentsPermissions, this.itemCache.sharing.documentsPermissions) ||
                    !this.compare(currentItem.userGroups, this.itemCache.sharing.userGroups)
                ) {
                    isNeedToUpdate = true;
                }
            }

            if (isNeedToUpdate) {
                swal({
                    title: 'You have changed permissions.',
                    text: 'Do you want to apply this change to all subfolders and files as well?',
                    type: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#DD6B55',
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    closeOnConfirm: true
                }, (folderPermissionApply) => {

                    if (folderPermissionApply) {
                        applyCascade = 1;
                    }
                    this.updateData(data, applyCascade, edit);
                });
            } else {
                this.updateData(data, applyCascade, edit);
            }

        } else {
            this.updateData(data, applyCascade, edit);
        }
    }

    updateData(data, applyCascade, edit) { // @todo edit this
        data.applyCascade = applyCascade; // @todo why additional parameter? why not in "data", which btw does not tell anything, should be typed
        if (edit) {
            delete data.isPublic;
        }
        const url = `/api/v1/documents/${this.selectedItemData.id}`; // problem?!

        this.api.patch('domainDocuments', url, data).subscribe(
            res => {
                Object.keys(this.formFields).forEach(property => this.formFields[property] = false);
                this.currentDirectoryId.next(this.currentDirectoryId.getValue());
                toastr.success('Updated successfull');
                this.isUpdating = false;
                this.isPermissionChanged = false;
                if (edit) {
                    this.hideEditPermissionModal();
                }
            }, error => {
                // console.log('DocumentsService -> updateData -> error', error)
                if (error.status == 422) {
                    if (JSON.parse(error._body).message) {
                        toastr.error(JSON.parse(error._body).message);
                    }
                    /* if (error.statusText) {
                        toastr.error(error.statusText);
                    } */
                }
                toastr.error('Failed to update.');
                this.isUpdating = false;
            }
        );
    }

    getDocument(id: number) {
        return this.api.getJson('domainDocuments', `/api/v1/documents/${id}`);
    }

    onCorporationChange(newValue) {
        if (this.isEditPermissionModalShow) {
            this.isPermissionChanged = true;
        }
        this.selectedItemData.buildings = this.selectedItemData.buildings.filter(b => {
            return this.buildings.find(building => b.id == building.id && (<any>building).corporationNumber != newValue.id);
        });
        this.multiselectBuildings = this.buildings.filter(b => newValue.find(item => item.id == (<any>b).corporationNumber));
        this.selectedItemData.buildings = [];
    }

    setItemCache({corporationNumbers, buildings, sharing: {isPublic, documentsPermissions, userGroups}}) {
        this.itemCache = {
            corporationNumbers: corporationNumbers,
            buildings: buildings,
            sharing: {
                isPublic: isPublic,
                documentsPermissions: documentsPermissions,
                userGroups: userGroups
            }
        };
    }

    clearItemCache() {
        return {
            corporationNumbers: [],
            buildings: [],
            sharing: {
                isPublic: null,
                documentsPermissions: [],
                userGroups: []
            }
        };
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/documents.service.ts